/**
 * AI Assistant Alpine.js Component
 *
 * Provides the reactive UI state for the AI Assistant FAB.
 * Works with the ai-assistant Stimulus controller for ActionCable integration.
 *
 * Conversations are persisted server-side. The frontend tracks the active
 * conversationId and loads/creates conversations via the API.
 *
 * Usage in HTML:
 *   <div x-data="aiAssistant()" ...>
 *
 * Or initialize with context:
 *   <div x-data="aiAssistant({ type: 'property', id: '123', label: '42 Oak Street' })" ...>
 */
export function aiAssistant(initialContext = null) {
  return {
    // UI State
    open: false,
    showSidebar: false,
    showSettings: false,
    isThinking: false,
    isConnected: false,
    scrollingToTxnId: null,
    loadingConversations: false,
    loadingMessages: false,
    aiOpenedSlideover: false, // true when the AI just opened a slideover (hides AI backdrop)

    // Chat State
    input: "",
    messages: [],
    activeChatId: null, // Server-assigned conversation ID (or null for new chat)

    // Suggested-prompt chips seeded by a springboard (e.g. a Decision opening
    // the FAB scoped to a property). Shown alongside the context quick actions
    // until the conversation starts. Cleared once a message is sent.
    springboardPrompts: [],

    // Context menu
    contextMenu: {
      show: false,
      x: 0,
      y: 0,
      chatId: null
    },

    // Settings
    settings: {
      aiName: "Assistant",
      userName: "",
      sendAs: "you"
    },

    // Persisted conversations (loaded from server)
    chats: [],

    // Current page context (set by Stimulus controller)
    currentContext: initialContext || {
      type: "general",
      label: "",
      id: ""
    },

    // Initialize on component load
    init() {
      console.debug("[AI FAB] init() called - initialContext:", this.currentContext)

      // Load context from page element (inside Turbo frame) - this is the primary source
      this.updateContextFromPage()

      // Defer loading conversations to avoid competing for Puma threads on page load
      setTimeout(() => this.loadConversations(), 3000)

      console.debug("[AI FAB] after init - messages.length:", this.messages.length)

      // Listen for ActionCable events from Stimulus controller
      this.setupEventListeners()

      // Listen for Turbo navigation to update context when page changes
      document.addEventListener("turbo:load", () => {
        console.debug("[AI FAB] turbo:load event")
        this.updateContextFromPage()
      })

      // Also listen for turbo:render for cached page visits
      document.addEventListener("turbo:render", () => {
        console.debug("[AI FAB] turbo:render event")
        this.updateContextFromPage()
      })

      // Listen for turbo-frame loads (sidebar navigation uses data-turbo-frame="main")
      document.addEventListener("turbo:frame-load", (event) => {
        if (event.target.id === "main") {
          console.debug("[AI FAB] turbo:frame-load event on main frame")
          this.updateContextFromPage()
        }
      })

      // When the assistant opens or closes: the CSS variable every slideover's
      // backdrop and container stop at (.work-layer), a body class for the
      // header chip, and the rail. Opening collapses the rail; closing restores
      // it only if the assistant was what collapsed it.
      this.$watch('open', (isOpen) => {
        // --ai-panel-width follows body.assistant-open in CSS (--assistant-w,
        // what an 820px record leaves beside it); nothing is set inline.
        // The one exception: while the panel slides out, its width is held
        // at what it was, because the rail expanding again would otherwise
        // narrow it mid-slide and reflow the conversation.
        this.holdPanelWidth(!isOpen)
        document.body.classList.toggle('assistant-open', isOpen)
        window.dispatchEvent(new CustomEvent('ai-panel-toggled', { detail: { open: isOpen } }))

        const body = window.Alpine?.$data(document.body)
        if (body && typeof body.toggleRail === 'function' && window.matchMedia('(min-width: 1024px)').matches) {
          if (isOpen && !body.railCollapsed) {
            body.toggleRail()
            this._railCollapsedForAssistant = true
          } else if (!isOpen && this._railCollapsedForAssistant) {
            this._railCollapsedForAssistant = false
            if (body.railCollapsed) body.toggleRail()
          }
        }

        // Closing the panel ends any springboard scoping.
        if (!isOpen) {
          this._springboardLock = false
          this.springboardPrompts = []
        }
      })

      // Springboard: another part of the app (e.g. a Decision) opens the FAB
      // scoped to a context with a couple of suggested prompts to tap.
      window.addEventListener('open-ai-assistant', (e) => this.openWithContext(e.detail || {}))

      // The Assistant chip in every page header, and `]`
      window.addEventListener('toggle-ai-assistant', () => {
        this.open = !this.open
        if (this.open) this.updateContextFromPage()
      })

      // Context follows what is on top. A slideover opening or closing moves
      // what the assistant is looking at without touching the conversation;
      // the record's label arrives once the slideover has fetched it, so look
      // twice. These are the open-* events the slideovers answer to.
      const SLIDEOVER_EVENTS = [
        'open-property-slideover', 'open-tenancy-slideover', 'open-person-slideover',
        'open-landlord-slideover', 'open-contractor-slideover', 'open-application-slideover',
        'open-key-slideover', 'open-issue-slideover', 'open-instruction-slideover',
        'open-valuation-slideover', 'open-rent-guarantee-slideover',
        'account-slideover-closed', 'slideover-closed'
      ]
      const followTop = () => {
        setTimeout(() => this.updateContextFromPage({ keepConversation: true }), 350)
        setTimeout(() => this.updateContextFromPage({ keepConversation: true }), 1500)
      }
      SLIDEOVER_EVENTS.forEach((name) => window.addEventListener(name, followTop))
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') followTop() })
      // And the slideovers store, which every record slideover registers with
      // on open and leaves on close, whatever event it did or did not send
      if (window.Alpine?.effect && window.Alpine.store('slideovers')) {
        let lastOpen = null
        window.Alpine.effect(() => {
          const open = Object.keys(window.Alpine.store('slideovers').openSlideovers || {}).join(',')
          if (lastOpen !== null && open !== lastOpen) followTop()
          lastOpen = open
        })
      }

      // Track scroll-to-transaction loading state
      window.addEventListener('scroll-to-transaction-started', (e) => {
        this.scrollingToTxnId = e.detail?.id || null
      })
      window.addEventListener('scroll-to-transaction-done', () => {
        this.scrollingToTxnId = null
      })

      // An account slideover closing used to close the floating panel with it.
      // The assistant is a column now: it stays, and looks at the page again.
      window.addEventListener('account-slideover-closed', () => {
        this._aiNavigatedRecently = false
        this.aiOpenedSlideover = false
      })
    },

    // Load persisted conversations from the server
    async loadConversations() {
      this.loadingConversations = true
      try {
        const response = await fetch("/api/ai_conversations", {
          headers: { "Accept": "application/json" }
        })
        if (response.ok) {
          const data = await response.json()
          this.chats = data.map(c => ({
            id: c.id,
            title: c.title || "New chat",
            context: c.contextType,
            contextId: c.contextId,
            icon: "ph-chat-circle",
            lastMessageAt: c.lastMessageAt,
            unread: 0
          }))

          // Auto-resume most recent conversation for current context if no active chat
          if (!this.activeChatId && this.chats.length > 0) {
            const contextChat = this.chats.find(c =>
              c.context === this.currentContext.type &&
              String(c.contextId || "") === String(this.currentContext.id || "")
            )
            if (contextChat) {
              this.loadConversation(contextChat.id)
            }
          }
        }
      } catch (e) {
        console.error("[AI FAB] Failed to load conversations:", e)
      } finally {
        this.loadingConversations = false
      }
    },

    // Load a specific conversation's messages from the server
    async loadConversation(conversationId) {
      this.loadingMessages = true
      try {
        const response = await fetch(`/api/ai_conversations/${conversationId}`, {
          headers: { "Accept": "application/json" }
        })
        if (response.ok) {
          const data = await response.json()
          this.activeChatId = data.id
          this.messages = (data.messages || []).map(m => ({
            role: m.role,
            content: m.content,
            richContent: m.richContent || {},
            showRichContent: !!m.richContent && Object.keys(m.richContent).length > 0,
            checkedGroups: m.richContent?.error_checklist
              ? new Array(m.richContent.error_checklist.length).fill(false)
              : undefined
          }))
          this.scrollToBottom()

          // Mark as read in sidebar
          const chat = this.chats.find(c => c.id === conversationId)
          if (chat) chat.unread = 0
        }
      } catch (e) {
        console.error("[AI FAB] Failed to load conversation:", e)
      } finally {
        this.loadingMessages = false
      }
    },

    // Update context from page element (looks for #ai-page-context inside the page)
    // Also checks for open tenancy slideover which overrides page context
    // What the "Looking at" line says: the record's label, else the page's
    // kind in plain words
    // Freeze the panel at its current width for the slide-out (sm+), and
    // let it go when the container's width transition ends.
    holdPanelWidth(hold) {
      const panel = this.$root.querySelector('.ai-fab-panel')
      if (!panel) return
      if (!hold || !window.matchMedia('(min-width: 640px)').matches) {
        panel.style.width = ''
        return
      }
      panel.style.width = `${panel.getBoundingClientRect().width}px`
      this.$root.addEventListener('transitionend', (e) => {
        if (e.target === this.$root && e.propertyName === 'width') panel.style.width = ''
      }, { once: true })
    },

    lookingAt() {
      const c = this.currentContext || {}
      if (c.label) return c.label
      const type = (c.type || 'this page').replace(/_/g, ' ')
      return type === 'general' ? 'this page' : type
    },

    updateContextFromPage({ keepConversation = false } = {}) {
      console.debug("[AI FAB] updateContextFromPage called")

      // A springboard has pinned the context (e.g. a Decision scoped the FAB to
      // a specific property). Don't let the underlying page's context clobber it
      // while that conversation is open — released when the panel closes.
      if (this._springboardLock) {
        console.debug("[AI FAB] Skipping context update — springboard lock active")
        return
      }

      const previousContext = this.currentContext
      let newContext = null

      // First, check if an account slideover is open - this takes priority
      const slideoverContext = this.getSlideoverContext()
      if (slideoverContext) {
        console.debug("[AI FAB] Using slideover context:", slideoverContext)
        newContext = slideoverContext
      } else {
        // Look for the context element inside the page content
        const contextEl = document.getElementById("ai-page-context")
        console.debug("[AI FAB] Context element found:", contextEl)

        if (contextEl) {
          try {
            const parsed = JSON.parse(contextEl.dataset.context || "{}")
            console.debug("[AI FAB] Parsed context:", parsed)
            if (parsed.type) {
              newContext = parsed
            }
          } catch (e) {
            console.error("[AI FAB] Failed to parse context:", e)
          }
        } else {
          console.warn("[AI FAB] No #ai-page-context element found - trying URL-based detection")
          const path = window.location.pathname
          let detectedType = "general"

          if (path.startsWith("/viewings")) {
            detectedType = "viewings"
          } else if (path.startsWith("/tenancies")) {
            detectedType = "tenancies"
          } else if (path.startsWith("/properties/") && !path.includes("/new")) {
            detectedType = "property"
          } else if (path.startsWith("/properties")) {
            detectedType = "properties"
          }

          console.debug("[AI FAB] URL-based context detection:", path, "->", detectedType)
          newContext = { type: detectedType, label: "", id: "" }
        }
      }

      if (newContext) {
        // Preserve current conversation when context changes — it's persisted on the server.
        // Start fresh UI for the new context instead of destroying messages.
        const contextChanged = previousContext.type !== newContext.type || previousContext.id !== newContext.id
        if (contextChanged && this.messages.length > 0 && !this._aiNavigatedRecently && !keepConversation) {
          console.debug("[AI FAB] Context changed, starting fresh for new context")
          this.messages = []
          this.activeChatId = null
          this.isThinking = false

          // Auto-resume most recent conversation for the new context
          const contextChat = this.chats.find(c =>
            c.context === newContext.type &&
            String(c.contextId || "") === String(newContext.id || "")
          )
          if (contextChat) {
            this.loadConversation(contextChat.id)
          }
        }

        this.currentContext = newContext
      }

      console.debug("[AI FAB] Final currentContext:", this.currentContext)
    },

    // Check if an account slideover is open and return its context
    getSlideoverContext() {
      // Check each account slideover type
      const slideoverTypes = [
        {
          selector: "[x-data*='tenancyAccountSlideover']",
          type: "tenancy",
          idKey: "tenancyId",
          labelKey: "tenancy",
          labelFn: (data) => data.tenancy?.property_name || "Tenancy"
        },
        {
          selector: "[x-data*='landlordAccountSlideover']",
          type: "landlord",
          idKey: "landlordId",
          labelKey: "landlord",
          labelFn: (data) => data.landlord?.name || "Landlord"
        },
        {
          selector: "[x-data*='contractorSlideover']",
          type: "contractor",
          idKey: "contractorId",
          labelKey: "contractor",
          labelFn: (data) => data.contractor?.display_name || data.contractor?.name || "Contractor"
        },
        {
          selector: "[x-data*='agencyAccountSlideover']",
          type: "agency",
          idKey: "agencyId",
          labelKey: "agency",
          labelFn: (data) => data.agency?.name || "Agency"
        },
        {
          selector: "[x-data^='bankAccountSlideover']",
          type: "bank",
          idKey: null,
          labelKey: "bankAccount",
          labelFn: (data) => data.bankAccount?.label || "Client Money Account"
        }
      ]

      for (const config of slideoverTypes) {
        const el = document.querySelector(config.selector)
        if (!el) continue

        const data = Alpine.$data(el)
        if (!data || !data.open) continue
        // Skip if an idKey is expected but missing (bank account has no ID)
        if (config.idKey && !data[config.idKey]) continue

        return {
          type: config.type,
          id: config.idKey ? String(data[config.idKey]) : "",
          label: config.labelFn(data)
        }
      }

      // Check the property slideover
      const propertySlideover = document.querySelector("[x-data*='propertySlideover']")
      if (propertySlideover) {
        const data = Alpine.$data(propertySlideover)
        if (data?.open && data?.propertyId) {
          return {
            type: "property",
            id: String(data.propertyId),
            label: data.property?.full_address || "Property"
          }
        }
      }

      // Also check the general landlord slideover (used on Landlords index and Accounts index)
      // Use startsWith match to avoid also matching landlordAccountSlideover
      const landlordSlideover = document.querySelector("[x-data^='landlordSlideover']")
      if (landlordSlideover) {
        const data = Alpine.$data(landlordSlideover)
        if (data?.open && data?.landlordId) {
          return {
            type: "landlord",
            id: String(data.landlordId),
            label: data.landlord?.display_name || data.landlord?.name || "Landlord"
          }
        }
      }

      // Also check the tenancy detail slideover (non-account)
      const tenancySlideover = document.querySelector("[x-data*='tenancySlideover']")
      if (tenancySlideover) {
        const data = Alpine.$data(tenancySlideover)
        if (data?.open) {
          if (data.mode === "view" && data.tenancy?.id) {
            return {
              type: "tenancy",
              id: String(data.tenancy.id),
              label: data.tenancy.property_address || "Tenancy"
            }
          }
          if (data.mode === "create") {
            return { type: "tenancy_create", id: "", label: "New Tenancy" }
          }
        }
      }

      // Application slideover (tenancy-applications index, dashboard, etc.).
      // ContextResolver grounds a tenancy_application id straight to its property
      // and scopes tools to the focal application.
      const applicationSlideover = document.querySelector("[x-data*='applicationSlideover']")
      if (applicationSlideover) {
        const data = Alpine.$data(applicationSlideover)
        if (data?.open && data?.applicationId) {
          return {
            type: "tenancy_application",
            id: String(data.applicationId),
            label: data.application?.property_display_name || "Application"
          }
        }
      }

      // Viewing slideover — ContextResolver resolves the property via the viewing.
      const viewingSlideover = document.querySelector("[x-data*='viewingSlideover']")
      if (viewingSlideover) {
        const data = Alpine.$data(viewingSlideover)
        if (data?.open && data?.viewingId) {
          return {
            type: "viewing",
            id: String(data.viewingId),
            label: data.viewing?.property?.display_name || "Viewing"
          }
        }
      }

      // Person slideover (tenants / people). Grounds the focal person.
      const personSlideover = document.querySelector("[x-data*='personSlideover']")
      if (personSlideover) {
        const data = Alpine.$data(personSlideover)
        if (data?.open && data?.personId) {
          return {
            type: "person",
            id: String(data.personId),
            label: data.person?.full_name || data.person?.name || "Person"
          }
        }
      }

      return null
    },

    setupEventListeners() {
      const controller = this.$el.closest("[data-controller*='ai-assistant']")
      if (!controller) return

      // ActionCable connection events
      controller.addEventListener("ai-assistant:connected", () => {
        this.isConnected = true
      })

      controller.addEventListener("ai-assistant:disconnected", () => {
        this.isConnected = false
      })

      // AI response events
      controller.addEventListener("ai-assistant:thinking", () => {
        this.handleThinking()
      })

      controller.addEventListener("ai-assistant:token", (event) => {
        this.handleToken(event.detail)
      })

      controller.addEventListener("ai-assistant:complete", (event) => {
        this.handleComplete(event.detail)
      })

      controller.addEventListener("ai-assistant:error", (event) => {
        this.handleError(event.detail)
      })
    },

    // ActionCable event handlers
    handleThinking() {
      this.isThinking = true
      this.messages.push({
        role: "assistant",
        thinking: true
      })
      this.scrollToBottom()
    },

    handleToken(data) {
      // Find the current assistant message being typed
      const lastMessage = this.messages[this.messages.length - 1]
      if (lastMessage?.role === "assistant") {
        if (lastMessage.thinking) {
          // Convert from thinking to typing
          lastMessage.thinking = false
          lastMessage.content = ""
          lastMessage.typing = true
          lastMessage.richContent = {}
          lastMessage.showRichContent = false
        }
        // Append token
        lastMessage.content += data.token
        this.scrollToBottom()
      }
    },

    handleComplete(data) {
      const lastMessage = this.messages[this.messages.length - 1]
      if (lastMessage?.role === "assistant") {
        lastMessage.typing = false
        lastMessage.content = data.content || lastMessage.content

        // Handle rich content if provided
        if (data.richContent) {
          lastMessage.richContent = data.richContent

          // Initialize checklist state for error groups
          if (data.richContent.error_checklist) {
            lastMessage.checkedGroups = new Array(data.richContent.error_checklist.length).fill(false)
          }

          setTimeout(() => {
            lastMessage.showRichContent = true
            this.scrollToBottom()
          }, 200)

          // Handle navigation and/or events from richContent
          const hasNavigate = !!data.richContent.navigate
          const hasEvents = data.richContent.events?.length > 0

          if (hasNavigate && hasEvents) {
            this._aiNavigatedRecently = true
            this.aiOpenedSlideover = true
            const events = data.richContent.events
            const alreadyOnPage = window.location.pathname === data.richContent.navigate

            if (alreadyOnPage) {
              // Already on the target page — just dispatch events directly
              events.forEach(evt => {
                window.dispatchEvent(new CustomEvent(evt.event, { detail: evt.detail || {} }))
              })
            } else {
              // Navigate within frame, then dispatch events after load
              const navFrame = document.querySelector('turbo-frame#main')
              if (navFrame) {
                const onLoad = () => {
                  navFrame.removeEventListener('turbo:frame-load', onLoad)
                  setTimeout(() => {
                    events.forEach(evt => {
                      window.dispatchEvent(new CustomEvent(evt.event, { detail: evt.detail || {} }))
                    })
                  }, 300)
                }
                navFrame.addEventListener('turbo:frame-load', onLoad)
                navFrame.src = data.richContent.navigate
                history.pushState({}, '', data.richContent.navigate)
              } else {
                Turbo.visit(data.richContent.navigate)
              }
            }
          } else if (hasEvents) {
            // Just dispatch events (slideover on current page)
            this._aiNavigatedRecently = true
            this.aiOpenedSlideover = true
            data.richContent.events.forEach(evt => {
              window.dispatchEvent(new CustomEvent(evt.event, { detail: evt.detail || {} }))
            })
          } else if (hasNavigate) {
            // Just navigate (no slideover) — use frame nav so AI panel stays open
            const frame = document.querySelector('turbo-frame#main')
            if (frame) {
              frame.src = data.richContent.navigate
              history.pushState({}, '', data.richContent.navigate)
            } else {
              Turbo.visit(data.richContent.navigate)
            }
          }
        }
      }
      this.isThinking = false

      // Capture the server-assigned conversation ID
      if (data.conversationId) {
        const isNewConversation = !this.activeChatId
        this.activeChatId = data.conversationId

        // Add to sidebar if this is a new conversation
        if (isNewConversation) {
          const existingChat = this.chats.find(c => c.id === data.conversationId)
          if (!existingChat) {
            const firstUserMsg = this.messages.find(m => m.role === "user")
            this.chats.unshift({
              id: data.conversationId,
              title: firstUserMsg ? (firstUserMsg.content.length > 30 ? firstUserMsg.content.substring(0, 30) + "..." : firstUserMsg.content) : "New chat",
              context: this.currentContext.type,
              contextId: this.currentContext.id,
              icon: "ph-chat-circle",
              lastMessageAt: new Date().toISOString(),
              unread: 0
            })
          }
        }
      }
    },

    handleError(data) {
      this.isThinking = false
      // Remove thinking indicator if present
      const lastMessage = this.messages[this.messages.length - 1]
      if (lastMessage?.thinking) {
        this.messages.pop()
      }
      // Show error message
      this.messages.push({
        role: "assistant",
        content: data.message || "Sorry, something went wrong. Please try again.",
        isError: true
      })
      this.scrollToBottom()
    },

    // Detect if the user's message is a navigation request (e.g. "show me Karen Seager's account")
    // Returns the search query if it's a nav request, or null if it should go to Claude
    parseNavigationIntent(message) {
      // Strip trailing politeness before pattern matching
      const cleaned = message.replace(/\s*(?:please|pls|thanks|thank\s+you|cheers|ta)\s*[.!?]?\s*$/i, '').trim()
      const lower = cleaned.toLowerCase()
      // Patterns that indicate navigation intent
      const navPatterns = [
        // "show me the account for Irwin Alvisse" / "open the tenancy for 26 Kingston Close"
        /^(?:show\s+me|open|go\s+to|take\s+me\s+to|navigate\s+to|find|pull\s+up|look\s+up)\s+(?:the\s+)?(?:account|tenancy|page|profile|details|record|property)\s+(?:for|of)\s+(.+?)\s*$/i,
        // "show me Irwin Alvisse's account" / "open Karen Seager's tenancy"
        /^(?:show\s+me|open|go\s+to|take\s+me\s+to|navigate\s+to|find|pull\s+up|look\s+up)\s+(.+?)(?:'s)?(?:\s+(?:account|tenancy|page|profile|details|record|property))?\s*$/i,
        // "show me the Irwin Alvisse account"
        /^(?:show\s+me|open|go\s+to|take\s+me\s+to)\s+(?:the\s+)?(.+?)(?:\s+(?:account|tenancy|page|profile|details|record))?\s*$/i
      ]

      // Simple page navigation (no entity lookup needed)
      const pagePatterns = {
        properties: /(?:properties|property)\s*(?:page|list|index)?$/i,
        tenancies: /(?:tenancies|tenancy)\s*(?:page|list|index)?$/i,
        viewings: /(?:viewings?)\s*(?:page|list|index)?$/i,
        enquiries: /(?:enquiries|enquiry)\s*(?:page|list|index)?$/i,
        landlords: /(?:landlords?)\s*(?:page|list|index)?$/i,
        contractors: /(?:contractors?)\s*(?:page|list|index)?$/i,
        accounting: /(?:accounts?|accounting)\s*(?:page|list|index)?$/i,
        jobs: /(?:jobs?|maintenance)\s*(?:page|list|index)?$/i,
        decisions: /(?:decisions?)\s*(?:page|list)?$/i,
        keys: /(?:keys?)\s*(?:page|list|index)?$/i,
        inspections: /(?:inspections?)\s*(?:page|list|index)?$/i,
        deposits: /(?:deposits?)\s*(?:page|list|index)?$/i,
        applications: /(?:applications?)\s*(?:page|list|index)?$/i
      }

      // Check for simple page navigation first
      for (const [page, pattern] of Object.entries(pagePatterns)) {
        if (lower.match(/^(?:show\s+me|open|go\s+to|take\s+me\s+to|navigate\s+to)\s+(?:the\s+)?/) && lower.match(pattern)) {
          return { type: "page", page }
        }
      }

      // Check for entity navigation
      for (const pattern of navPatterns) {
        const match = cleaned.match(pattern)
        if (match) {
          let query = match[1].trim()
          // Clean up common words
          query = query.replace(/^(?:the|a|an)\s+/i, '').trim()
          if (query.length >= 2) {
            return { type: "entity", query }
          }
        }
      }

      return null
    },

    // Page URL mapping for simple navigation
    pageUrls: {
      properties: "/properties",
      tenancies: "/tenancies",
      viewings: "/viewings",
      enquiries: "/enquiries",
      landlords: "/landlords",
      contractors: "/contractors",
      accounting: "/accounting/accounts",
      jobs: "/maintenance",
      decisions: "/decisions",
      keys: "/keys",
      inspections: "/inspections",
      deposits: "/deposits",
      applications: "/tenancy-applications"
    },

    // Handle fast navigation (no Claude needed)
    async handleNavigation(intent, userMessage) {
      if (intent.type === "page") {
        // Simple page navigation — immediate
        const url = this.pageUrls[intent.page]
        if (url) {
          if (window.location.pathname === url) {
            // Already on this page — no navigation needed
            this.messages.push({ role: "assistant", content: "You're already on this page." })
            this.scrollToBottom()
          } else {
            this.messages.push({ role: "assistant", content: "Opening now..." })
            this.scrollToBottom()
            this._aiNavigatedRecently = true
            const frame = document.querySelector('turbo-frame#main')
            if (frame) {
              frame.src = url
              history.pushState({}, '', url)
            } else {
              Turbo.visit(url)
            }
          }
          return true
        }
      }

      if (intent.type === "entity") {
        // Entity navigation — fast server-side lookup
        this.isThinking = true
        this.messages.push({ role: "assistant", thinking: true })
        this.scrollToBottom()

        try {
          const response = await fetch("/api/ai_navigate", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
              "Accept": "application/json"
            },
            body: JSON.stringify({ query: intent.query })
          })

          const data = await response.json()

          // Remove thinking indicator
          const lastMsg = this.messages[this.messages.length - 1]
          if (lastMsg?.thinking) this.messages.pop()
          this.isThinking = false

          if (data.found) {
            this.messages.push({ role: "assistant", content: `Opening ${data.label}...` })
            this.scrollToBottom()
            this._aiNavigatedRecently = true
            if (data.event) this.aiOpenedSlideover = true

            const alreadyOnPage = window.location.pathname === data.navigate
            const frame = document.querySelector('turbo-frame#main')

            if (data.event && alreadyOnPage) {
              // Already on the target page — just dispatch the event directly
              window.dispatchEvent(new CustomEvent(data.event, { detail: data.detail || {} }))
            } else if (data.event && frame) {
              // Navigate within frame, then open slideover after frame loads
              const onFrameLoad = () => {
                frame.removeEventListener('turbo:frame-load', onFrameLoad)
                setTimeout(() => {
                  window.dispatchEvent(new CustomEvent(data.event, { detail: data.detail || {} }))
                }, 300)
              }
              frame.addEventListener('turbo:frame-load', onFrameLoad)
              frame.src = data.navigate
              history.pushState({}, '', data.navigate)
            } else if (frame) {
              // Just navigate within frame
              frame.src = data.navigate
              history.pushState({}, '', data.navigate)
            } else {
              Turbo.visit(data.navigate)
            }
            return true
          }
          // Not found — fall through to Claude
          return false
        } catch (e) {
          console.error("[AI FAB] Navigation lookup failed:", e)
          // Remove thinking indicator and fall through to Claude
          const lastMsg = this.messages[this.messages.length - 1]
          if (lastMsg?.thinking) this.messages.pop()
          this.isThinking = false
          return false
        }
      }

      return false
    },

    // Send message via Stimulus controller
    sendMessage() {
      if (!this.input.trim() || this.isThinking) return

      // Refresh context in case a slideover opened since page load
      this.updateContextFromPage()

      const userMessage = this.input.trim()

      // Add user message to UI
      this.messages.push({
        role: "user",
        content: userMessage
      })

      this.input = ""
      this.scrollToBottom()

      // Check for navigation intent — handle without Claude if possible
      const navIntent = this.parseNavigationIntent(userMessage)
      if (navIntent) {
        this.handleNavigation(navIntent, userMessage).then(handled => {
          if (!handled) {
            // Not found — send to Claude for a full AI response
            this.sendToAI(userMessage)
          }
        })
        return
      }

      // Send to Claude for AI processing
      this.sendToAI(userMessage)
    },

    // Send message to Claude via ActionCable
    sendToAI(userMessage) {
      // Update chat title in sidebar if this is the first message
      if (this.activeChatId) {
        const chat = this.chats.find(c => c.id === this.activeChatId)
        if (chat && chat.title === "New chat") {
          chat.title = userMessage.length > 30 ? userMessage.substring(0, 30) + "..." : userMessage
        }
      }

      const controller = this.$el.closest("[data-controller*='ai-assistant']")
      if (controller) {
        const stimulusController = this.$el.closest("[data-controller*='ai-assistant']").__stimulusController ||
                                   window.Stimulus?.getControllerForElementAndIdentifier(controller, "ai-assistant")
        if (stimulusController) {
          stimulusController.sendMessage(userMessage, this.currentContext, this.activeChatId)
        } else {
          this.simulateResponse()
        }
      } else {
        this.simulateResponse()
      }
    },

    // Simulated response for prototype/demo mode
    simulateResponse() {
      this.isThinking = true

      // Add thinking indicator
      this.messages.push({
        role: "assistant",
        thinking: true
      })
      this.scrollToBottom()

      // Simulate thinking delay
      const thinkingTime = 1000 + Math.random() * 1000

      setTimeout(() => {
        // Remove thinking indicator
        this.messages.pop()

        // Get random stubbed response
        const response = this.stubbedResponses[Math.floor(Math.random() * this.stubbedResponses.length)]

        // Add message with empty content for typing effect
        this.messages.push({
          role: "assistant",
          content: "",
          typing: true,
          richContent: {
            images: response.images || null,
            links: response.links || null,
            actions: response.actions || null,
            code: response.code || null
          },
          showRichContent: false
        })

        const messageIndex = this.messages.length - 1
        this.typeResponse(response.text, messageIndex)
      }, thinkingTime)
    },

    // Typing effect for responses
    typeResponse(fullText, messageIndex) {
      let charIndex = 0
      const baseSpeed = 8
      const variation = 6

      const typeNextChar = () => {
        if (charIndex < fullText.length) {
          this.messages[messageIndex].content = fullText.substring(0, charIndex + 1)
          charIndex++
          this.scrollToBottom()

          const delay = baseSpeed + Math.random() * variation
          const char = fullText[charIndex - 1]
          const punctuationDelay = ['.', '!', '?', ','].includes(char) ? 40 : 0

          setTimeout(typeNextChar, delay + punctuationDelay)
        } else {
          // Typing complete
          this.messages[messageIndex].typing = false
          this.isThinking = false

          // Show rich content
          setTimeout(() => {
            this.messages[messageIndex].showRichContent = true
            this.scrollToBottom()
          }, 200)
        }
      }

      typeNextChar()
    },

    // Context-aware quick actions configuration
    get contextQuickActions() {
      const contextType = this.currentContext?.type || "general"
      console.debug("[AI FAB] contextQuickActions - contextType:", contextType, "currentContext:", this.currentContext)

      const actionsByContext = {
        tenancies: [
          {
            id: "add_tenancy",
            label: "Add Tenancy",
            icon: "ph-plus",
            type: "event",
            event: "open-tenancy-slideover-create",
            context: {}
          },
          {
            id: "analyze",
            label: "Analyze tenancies",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Analyze my current tenancies and give me insights on occupancy, rent trends, and upcoming renewals."
          }
        ],
        viewings: [
          {
            id: "add_viewing",
            label: "Add Viewing",
            icon: "ph-plus",
            type: "event",
            event: "open-viewing-slideover-create",
            context: {}
          },
          {
            id: "analyze",
            label: "Analyze viewings",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Analyze my upcoming viewings and give me insights on scheduling, conversion rates, and property interest."
          }
        ],
        property: [
          {
            id: "add_tenancy",
            label: "Add Tenancy",
            icon: "ph-plus",
            type: "event",
            event: "open-tenancy-slideover-create",
            context: {
              property_id: this.currentContext?.id,
              property_name: this.currentContext?.label,
              full_address: this.currentContext?.full_address
            }
          },
          {
            id: "suggest",
            label: "Suggest improvements",
            icon: "ph-sparkle",
            type: "prompt",
            prompt: "What improvements would you suggest for this property listing?"
          },
          {
            id: "describe",
            label: "Write description",
            icon: "ph-pencil-simple-line",
            type: "prompt",
            prompt: "Can you write a compelling description for this property?"
          }
        ],
        // Agency account actions
        agency: [
          {
            id: "summary",
            label: "Account summary",
            icon: "ph-chart-pie-slice",
            type: "prompt",
            prompt: "Give me a summary of the agency account — how much is available to withdraw, awaiting payment, and invoiced this month?"
          },
          {
            id: "this_month",
            label: "This month's fees",
            icon: "ph-calendar",
            type: "prompt",
            prompt: "Break down all the fees invoiced this month by property."
          },
          {
            id: "drawdowns",
            label: "Recent drawdowns",
            icon: "ph-arrow-square-out",
            type: "prompt",
            prompt: "Show me any recent drawdowns from the agency account."
          }
        ],
        // Tenancy account actions
        tenancy: [
          {
            id: "summary",
            label: "Account summary",
            icon: "ph-chart-pie-slice",
            type: "prompt",
            prompt: "Give me a summary of this tenancy account — balance, rent, deposit, and recent transactions."
          },
          {
            id: "rent_status",
            label: "Rent status",
            icon: "ph-currency-gbp",
            type: "prompt",
            prompt: "Is the rent up to date on this tenancy? Show me the recent rent history."
          },
          {
            id: "find_errors",
            label: "Help me find errors",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Look through the legacy entries on this account and help me find any that look like errors, transfers, reversals, adjustments, or duplicates that shouldn't be there. These are often leftover guff from the old system import. If you find any pairs that net to zero, suggest them for removal."
          }
        ],
        // Landlord account actions
        landlord: [
          {
            id: "summary",
            label: "Account summary",
            icon: "ph-chart-pie-slice",
            type: "prompt",
            prompt: "Give me a summary of this landlord account — balance held, recent transactions, and any outstanding amounts."
          },
          {
            id: "fees",
            label: "Recent fees",
            icon: "ph-receipt",
            type: "prompt",
            prompt: "Show me the recent management fees charged to this landlord."
          },
          {
            id: "find_errors",
            label: "Help me find errors",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Look through the legacy entries on this account and help me find any that look like errors, transfers, reversals, adjustments, or duplicates that shouldn't be there. These are often leftover guff from the old system import. If you find any pairs that net to zero, suggest them for removal."
          }
        ],
        // Contractor account actions
        contractor: [
          {
            id: "summary",
            label: "Account summary",
            icon: "ph-chart-pie-slice",
            type: "prompt",
            prompt: "Give me a summary of this contractor account — balance owed and recent transactions."
          },
          {
            id: "find_errors",
            label: "Help me find errors",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Look through the legacy entries on this account and help me find any that look like errors, transfers, reversals, adjustments, or duplicates that shouldn't be there. These are often leftover guff from the old system import. If you find any pairs that net to zero, suggest them for removal."
          }
        ],
        // Default/general actions
        general: [
          {
            id: "suggest",
            label: "Suggest improvements",
            icon: "ph-sparkle",
            type: "prompt",
            prompt: "What improvements would you suggest for this property listing?"
          },
          {
            id: "describe",
            label: "Write description",
            icon: "ph-pencil-simple-line",
            type: "prompt",
            prompt: "Can you write a compelling description for this property?"
          },
          {
            id: "analyze",
            label: "Analyze property",
            icon: "ph-magnifying-glass",
            type: "prompt",
            prompt: "Analyze this property and give me key insights."
          }
        ]
      }

      // Springboard-seeded prompts take precedence and lead the chip list, so a
      // Decision's suggested actions appear first when it opens the FAB.
      const base = actionsByContext[contextType] || actionsByContext.general
      if (this.springboardPrompts.length > 0) {
        return [ ...this.springboardPrompts, ...base ]
      }
      return base
    },

    // Quick action handlers
    quickAction(actionId) {
      // Find the action in context-aware actions
      const action = this.contextQuickActions.find(a => a.id === actionId)

      if (action) {
        if (action.type === "event") {
          // Dispatch window event for actions like opening slideovers
          window.dispatchEvent(new CustomEvent(action.event, {
            detail: action.context || {}
          }))
          // Close the panel after dispatching
          this.open = false
          return
        } else if (action.type === "prompt") {
          // Send as a chat message
          this.input = action.prompt
          this.sendMessage()
          return
        }
      }

      // Fallback to legacy behavior for backwards compatibility
      const prompts = {
        suggest: "What improvements would you suggest for this property listing?",
        describe: "Can you write a compelling description for this property?",
        analyze: "Analyze this property and give me key insights."
      }

      if (prompts[actionId]) {
        this.input = prompts[actionId]
        this.sendMessage()
      }
    },

    // --- Error checklist methods ---

    toggleErrorGroup(msgIndex, groupIndex) {
      const msg = this.messages[msgIndex]
      if (msg?.checkedGroups) {
        msg.checkedGroups[groupIndex] = !msg.checkedGroups[groupIndex]
        // Force Alpine reactivity by replacing the array
        msg.checkedGroups = [...msg.checkedGroups]
      }
    },

    toggleAllErrorGroups(msgIndex) {
      const msg = this.messages[msgIndex]
      if (!msg?.checkedGroups) return
      const allChecked = msg.checkedGroups.every(Boolean)
      msg.checkedGroups = msg.checkedGroups.map(() => !allChecked)
    },

    checkedGroupCount(msgIndex) {
      const msg = this.messages[msgIndex]
      return msg?.checkedGroups?.filter(Boolean).length || 0
    },

    removeSelectedErrors(msgIndex) {
      const msg = this.messages[msgIndex]
      if (!msg?.richContent?.error_checklist || !msg?.checkedGroups) return

      // Collect all entry IDs from checked groups
      const entryIds = []
      msg.richContent.error_checklist.forEach((group, i) => {
        if (msg.checkedGroups[i]) {
          entryIds.push(...group.entry_ids)
        }
      })

      if (entryIds.length === 0) return

      // Send confirmation command directly (bypasses Claude, goes to RemoveLegacyEntriesHandler)
      const command = `CONFIRM_ACTION:remove_legacy_entries:${entryIds.join(',')}`

      this.messages.push({
        role: "user",
        content: `Remove ${this.checkedGroupCount(msgIndex)} selected error groups (${entryIds.length} entries)`
      })
      this.scrollToBottom()

      const controller = this.$el.closest("[data-controller*='ai-assistant']")
      const stimulusCtrl = window.Stimulus?.getControllerForElementAndIdentifier(controller, "ai-assistant")
      if (stimulusCtrl) {
        stimulusCtrl.sendMessage(command, this.currentContext, this.activeChatId)
      }
    },

    handleAction(action) {
      console.log("Action clicked:", action.label)

      // sendMessage actions: send a command through ActionCable as a chat message
      if (action.sendMessage) {
        // Show friendly message in chat
        this.messages.push({
          role: "user",
          content: action.displayMessage || action.label
        })
        this.scrollToBottom()

        // Send command through ActionCable
        const controller = this.$el.closest("[data-controller*='ai-assistant']")
        const stimulusCtrl = window.Stimulus?.getControllerForElementAndIdentifier(controller, "ai-assistant")
        if (stimulusCtrl) {
          stimulusCtrl.sendMessage(action.sendMessage, this.currentContext, this.activeChatId)
        }
        return
      }

      // Dispatch window event if action has an event type
      if (action.event) {
        window.dispatchEvent(new CustomEvent(action.event, {
          detail: action.context || {}
        }))
      }

      // Show confirmation in chat
      this.messages.push({
        role: "assistant",
        content: `✓ ${action.label}`,
        richContent: {},
        showRichContent: false
      })
      this.scrollToBottom()

      // Close the chat panel after action (optional - provides good UX)
      if (action.closeOnAction !== false) {
        setTimeout(() => {
          this.open = false
        }, 300)
      }
    },

    // Chat management
    clearChat() {
      this.messages = []
      this.input = ""
      this.isThinking = false
      this.activeChatId = null
    },

    chatsByContext(contextType) {
      return this.chats.filter(c => c.context === contextType)
    },

    // Group all chats by context type for sidebar display
    get chatContextGroups() {
      const groups = []
      const seen = new Set()

      // Current context first (if not general)
      const currentType = this.currentContext?.type
      if (currentType && currentType !== "general") {
        const chats = this.chatsByContext(currentType)
        if (chats.length > 0) {
          groups.push({ label: this.currentContext.label || currentType, chats })
          seen.add(currentType)
        }
      }

      // All other non-general contexts
      for (const chat of this.chats) {
        if (chat.context === "general" || seen.has(chat.context)) continue
        seen.add(chat.context)
        const chats = this.chatsByContext(chat.context)
        groups.push({ label: chat.context, chats })
      }

      // General always last
      const generalChats = this.chatsByContext("general")
      if (generalChats.length > 0) {
        groups.push({ label: "General", chats: generalChats })
      }

      return groups
    },

    switchChat(chatId) {
      if (chatId === this.activeChatId) return
      this.loadConversation(chatId)
    },

    newChat() {
      this.messages = []
      this.activeChatId = null
      this.isThinking = false
      this.input = ""
    },

    // Springboard entry point: open the FAB scoped to a given context with a
    // couple of suggested prompts shown as tappable chips. Used by Decisions
    // (e.g. "Review 2 new applications" opens the FAB on that property with
    // "Summarise the applications" / "Email the landlord" prompts ready to go).
    //
    // detail: {
    //   context: { type, id, label },
    //   suggestedPrompts: [{ label, prompt, icon? }, ...]
    // }
    openWithContext(detail) {
      const context = detail.context || {}
      const prompts = Array.isArray(detail.suggestedPrompts) ? detail.suggestedPrompts : []

      // Fresh conversation so the suggested-prompt chips show (they render only
      // when there are no messages).
      this.newChat()

      if (context.type) {
        this.currentContext = {
          type: context.type,
          id: context.id != null ? String(context.id) : "",
          label: context.label || ""
        }
        // Pin this context for the life of the panel.
        this._springboardLock = true
      }

      this.springboardPrompts = prompts.map((p, i) => ({
        id: `springboard_${i}`,
        label: p.label,
        icon: p.icon || "ph-sparkle",
        type: "prompt",
        prompt: p.prompt
      }))

      this.open = true
    },

    async deleteChat(chatId) {
      this.closeContextMenu()

      // Call server to archive
      try {
        await fetch(`/api/ai_conversations/${chatId}`, {
          method: "DELETE",
          headers: {
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
            "Accept": "application/json"
          }
        })
      } catch (e) {
        console.error("[AI FAB] Failed to delete conversation:", e)
      }

      // Remove from sidebar
      const chatIndex = this.chats.findIndex(c => c.id === chatId)
      if (chatIndex !== -1) {
        this.chats.splice(chatIndex, 1)
      }

      // If we deleted the active chat, start fresh
      if (chatId === this.activeChatId) {
        this.newChat()
      }
    },

    // Context menu
    openContextMenu(event, chatId) {
      this.contextMenu = {
        show: true,
        x: event.clientX,
        y: event.clientY,
        chatId: chatId
      }
    },

    closeContextMenu() {
      this.contextMenu.show = false
    },

    async renameChat(chatId) {
      this.closeContextMenu()
      const chat = this.chats.find(c => c.id === chatId)
      if (!chat) return

      const newTitle = prompt("Enter new chat name:", chat.title)
      if (newTitle && newTitle.trim()) {
        chat.title = newTitle.trim()

        // Persist rename to server
        try {
          await fetch(`/api/ai_conversations/${chatId}`, {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
              "Accept": "application/json"
            },
            body: JSON.stringify({ title: newTitle.trim() })
          })
        } catch (e) {
          console.error("[AI FAB] Failed to rename conversation:", e)
        }
      }
    },

    clearChatMessages(chatId) {
      this.closeContextMenu()
      if (chatId === this.activeChatId) {
        this.messages = []
      }
    },

    // Simple markdown to HTML converter for assistant messages
    formatMarkdown(text) {
      if (!text) return ''

      // Process block-level elements first, then inline
      const lines = text.split('\n')
      const output = []
      let inList = false
      let listType = null
      let olCounter = 0

      for (const line of lines) {
        const headingMatch = line.match(/^(#{1,4})\s+(.+)$/)
        const bulletMatch = line.match(/^[-•]\s+(.+)$/)
        const numberedMatch = line.match(/^\d+\.\s+(.+)$/)
        const hrMatch = line.match(/^[-—–]{3,}$/)

        if (headingMatch) {
          if (inList) {
            output.push(listType === 'ul' ? '</ul>' : '</ol>')
            inList = false
            listType = null
            olCounter = 0
          }
          const level = headingMatch[1].length
          const styles = {
            1: 'text-[13px] font-medium mt-3 mb-1',
            2: 'text-[13px] font-medium mt-2.5 mb-0.5',
            3: 'text-[12.5px] font-medium mt-2 mb-0.5 text-[#4B4B48]',
            4: 'text-[12.5px] font-medium mt-1.5 mb-0.5 text-[#7D7D78]'
          }
          output.push(`<div class="${styles[level] || styles[4]}">${this._inlineMarkdown(headingMatch[2])}</div>`)
        } else if (hrMatch) {
          if (inList) {
            output.push(listType === 'ul' ? '</ul>' : '</ol>')
            inList = false
            listType = null
            olCounter = 0
          }
          output.push('<hr class="my-2 border-gray-200 dark:border-gray-700">')
        } else if (bulletMatch) {
          if (!inList || listType !== 'ul') {
            if (inList) output.push(listType === 'ul' ? '</ul>' : '</ol>')
            output.push('<ul class="my-1 ml-4 list-disc space-y-0.5">')
            inList = true
            listType = 'ul'
          }
          output.push(`<li>${this._inlineMarkdown(bulletMatch[1])}</li>`)
        } else if (numberedMatch) {
          olCounter++
          if (!inList || listType !== 'ol') {
            if (inList) output.push(listType === 'ul' ? '</ul>' : '</ol>')
            output.push('<ol class="my-1 ml-4 list-decimal space-y-0.5">')
            inList = true
            listType = 'ol'
          }
          output.push(`<li value="${olCounter}">${this._inlineMarkdown(numberedMatch[1])}</li>`)
        } else {
          if (inList) {
            output.push(listType === 'ul' ? '</ul>' : '</ol>')
            inList = false
            listType = null
          }
          if (line.trim() === '') {
            output.push('<div class="h-2"></div>')
          } else {
            output.push(`<div>${this._inlineMarkdown(line)}</div>`)
          }
        }
      }
      if (inList) output.push(listType === 'ul' ? '</ul>' : '</ol>')

      return output.join('')
    },

    // Inline markdown formatting (bold, italic, code, transaction links)
    _inlineMarkdown(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        .replace(/`([^`]+)`/g, '<code class="px-1 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-sm font-mono">$1</code>')
        // Transaction references: #LE12345 or #L1234 → clickable links
        .replace(/#(LE?\d+)/g, '<a href="#" class="text-brand-500 hover:text-brand-600 underline decoration-dotted font-mono text-xs" onclick="window.dispatchEvent(new CustomEvent(\'scroll-to-transaction\', { detail: { id: \'$1\' } })); return false">#$1</a>')
    },

    // Utility - scroll any visible chat container to bottom
    scrollToBottom() {
      // On mobile, use the global function exposed by the mobile chat panel
      if (window.innerWidth < 640 && typeof window.scrollMobileChatToBottom === 'function') {
        window.scrollMobileChatToBottom()
        return
      }

      // Desktop: use refs or query selector
      requestAnimationFrame(() => {
        const container = this.$refs?.chatMessages ||
                          document.querySelector('[x-ref="chatMessages"]')
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },

    // Stubbed responses for demo/prototype mode
    stubbedResponses: [
      {
        text: "I can help you create a new tenancy. Click below to get started.",
        actions: [
          {
            label: "Add Tenancy",
            icon: "ph-plus",
            event: "open-tenancy-slideover-create",
            context: {}
          }
        ]
      },
      {
        text: "Based on the property details, I'd suggest highlighting the south-facing garden in the description - it's a great selling point!"
      },
      {
        text: "I've analyzed the listing and found 3 areas for improvement: the photos could be reordered to show the kitchen first, the price seems competitive for the area, and adding the EPC rating would help.",
        actions: [
          { label: "Reorder photos", icon: "ph-images" },
          { label: "Add EPC rating", icon: "ph-lightning" }
        ]
      },
      {
        text: "The current rent of £1,500/month is slightly below market rate for this area. Similar 2-bed properties are averaging £1,650.",
        links: [
          { label: "View market comparison", url: "#market-report" },
          { label: "Rent review guide", url: "#rent-guide" }
        ]
      },
      {
        text: "I found some similar properties in the area that might help with your pricing strategy:",
        images: [
          { url: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=200&h=120&fit=crop", caption: "3 bed, £1,800/mo" },
          { url: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=200&h=120&fit=crop", caption: "2 bed, £1,650/mo" },
          { url: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=200&h=120&fit=crop", caption: "2 bed, £1,700/mo" }
        ]
      },
      {
        text: "I've drafted a property description for you based on the listing details:",
        code: "Stunning 2-bedroom apartment in the heart of the city. This beautifully presented property features a spacious open-plan living area, modern fitted kitchen, and a private balcony with panoramic views. Located moments from transport links and local amenities.",
        actions: [
          { label: "Copy to clipboard", icon: "ph-copy" },
          { label: "Use this description", icon: "ph-check" }
        ]
      }
    ]
  }
}

// Make available globally for Alpine
if (typeof window !== "undefined") {
  window.aiAssistant = aiAssistant

}

export default aiAssistant
